/* eslint-disable react/prop-types */
//
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { accountsSelectors } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';

import CheckIcon from '@mui/icons-material/DoneRounded';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { accountDiscoveryActions, accountDiscoverySelectors } from 'app-components/flux/account-discovery';
import { institutionLoginsActions, institutionLoginsSelectors } from 'app-components/flux/institution-logins';

import { InstitutionStatusBanner } from 'components/FiStatusBanner';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import StdDialogProgress from 'components/Dialogs/StdDialogProgress';
import InstitutionLoginCredentialsForm from '../InstitutionLoginCredentialsForm';
import MfaChallengeForm from '../MfaChallengeForm';

const Authenticator = forwardRef((props, ref) => {
  const {
    aggregator,
    cpSetupMode,
    fiBrandingIndent,
    fullAddAccountsDialog,
    hideInitialPartnerAuthIntro,
    institution,
    onCancel,
    provideForRefreshAccounts,
    scope,
    trackingProperties,
    fixFiMode,
  } = props;
  const dispatch = useDispatch();

  const [page, setPage] = useState(cpSetupMode !== 'PROVIDE-MFA' ? 'CREDENTIALS' : 'MFA');

  const error = useSelector((state) => accountDiscoverySelectors.getError(state, { scope }));
  const institutionLogin = useSelector((state) => accountDiscoverySelectors.getInstitutionLogin(state, { scope }));
  const isPolling = useSelector((state) => accountDiscoverySelectors.getIsPolling(state, { scope }));
  const isSubmitting = useSelector((state) => accountDiscoverySelectors.getIsSubmitting(state, { scope }));
  const mfaChallenge = useSelector((state) => accountDiscoverySelectors.getMfaChallenge(state, { scope }));

  const theme = useTheme();

  const fiStatusEnabled = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'fiStatus'));

  // these will only exist when we are updating an existing institution-login
  //
  const institutionLoginId = institutionLogin ? institutionLogin.id : null;
  const institutionLoginIsAmbigous = useSelector((state) =>
    institutionLoginsSelectors.isInstitutionLoginAmbiguous(state, { institutionLoginId }),
  );
  const accounts = useSelector((state) =>
    accountsSelectors.getAccountsForInstitutionLoginId(state, { institutionLoginId }),
  );

  const channelData = useSelector((state) => accountDiscoverySelectors.getInstitutionChannelData(state, { scope }));

  useMount(() => {
    // lets make sure we have the latest institution-logins (useful for ambiguous message)
    dispatch(institutionLoginsActions.getInstitutionLogins());
  });

  useImperativeHandle(ref, () => ({
    handleClose: (dEvent) => {
      if (provideForRefreshAccounts && dEvent !== 'SUBMIT_SUCCESS') {
        if (page === 'CREDENTIALS') {
          dispatch(
            institutionLoginsActions.refreshAccountsCredentialsNotProvided(
              institutionLogin ? institutionLogin.id : '0',
            ),
          );
        } else if (page === 'MFA') {
          dispatch(
            institutionLoginsActions.refreshAccountsMfaNotProvided(
              mfaChallenge ? mfaChallenge.institutionLoginId : '0',
            ),
          );
        }
      }
    },
  }));

  if (mfaChallenge && page !== 'MFA') {
    setPage('MFA');
  }

  const handleCredentialsSubmit = (institutionLoginWithCredentials, resolve, reject) => {
    if (provideForRefreshAccounts) {
      dispatch(
        institutionLoginsActions.refreshAccountsCredentialsProvided(
          { institutionLogin: institutionLoginWithCredentials },
          { scope, mode: trackingProperties?.mode },
        ),
      );
      resolve();
    } else {
      const accountDiscoveryCredentialsFormSubmitPromise = actionHelpers.bindPromiseAction(
        dispatch,
        accountDiscoveryActions.accountDiscoveryCredentialsFormSubmit,
      );
      accountDiscoveryCredentialsFormSubmitPromise(
        { institutionLogin: institutionLoginWithCredentials.set('cpSetupMode', cpSetupMode) },
        { scope, mode: trackingProperties?.mode },
      )
        .then(resolve, reject)
        .catch((error) => assert(false, error));
    }
  };

  const handleMFASubmit = (mfaResponse, resolve, reject) => {
    if (provideForRefreshAccounts) {
      const refreshAccountsMfaFormSubmitPromise = actionHelpers.bindPromiseAction(
        dispatch,
        institutionLoginsActions.refreshAccountsMfaFormSubmit,
      );
      refreshAccountsMfaFormSubmitPromise({ mfaResponse }, { scope, mode: trackingProperties?.mode })
        .then(resolve)
        .catch(reject);
    } else {
      const accountDiscoveryMfaFormSubmitPromise = actionHelpers.bindPromiseAction(
        dispatch,
        accountDiscoveryActions.accountDiscoveryMfaFormSubmit,
      );
      accountDiscoveryMfaFormSubmitPromise({ mfaResponse }, { scope, mode: trackingProperties?.mode })
        .then(() => {
          setPage('CREDENTIALS');
          resolve();
        })
        .catch(() => {
          setPage('CREDENTIALS');
          reject(Error('accountDiscoveryMfaFormSubmitPromise')); // TODO:Should we handle error on Mfa Form
        });
    }
  };

  const isBusy = isSubmitting || isPolling;
  return (
    <>
      {!fullAddAccountsDialog && institutionLoginIsAmbigous && (
        <Box bgcolor='background.default' display='flex' mt={1} mx={3} px={1} py={0.5}>
          <Typography variant='captionRegular' style={{ color: theme.palette.text.primary }}>
            {`Associated accounts :${accounts && accounts.map((account) => ` ${account && account.name}`).toArray()}`}
          </Typography>
        </Box>
      )}

      <Box display='flex' flex='1' flexDirection='column'>
        {isBusy ? (
          <StdDialogProgress icon={CheckIcon} label='Securing connection...' />
        ) : (
          <Box display='flex' flex='1' flexDirection='column'>
            {fiStatusEnabled && channelData?.authType !== 'PARTNER_AUTH' ? (
              <Box mt={1.5} mx={3} mb={0.5}>
                <InstitutionStatusBanner institutionId={institution.id} />
              </Box>
            ) : null}

            {page === 'CREDENTIALS' && (
              <InstitutionLoginCredentialsForm
                aggregator={aggregator}
                error={error}
                fiBrandingIndent={fiBrandingIndent}
                fullAddAccountsDialog={fullAddAccountsDialog}
                hideInitialPartnerAuthIntro={hideInitialPartnerAuthIntro}
                institutionLogin={institutionLogin}
                onCancel={onCancel}
                onSubmit={handleCredentialsSubmit}
                scope={scope}
                institution={institution}
                trackingProperties={trackingProperties}
                fixFiMode={fixFiMode}
              />
            )}
            {page === 'MFA' && (
              <MfaChallengeForm
                fiBrandingIndent={fiBrandingIndent}
                fullAddAccountsDialog={fullAddAccountsDialog}
                institutionLogin={institutionLogin}
                isSubmitting={isSubmitting}
                mfaChallenge={mfaChallenge}
                onCancel={onCancel}
                onSubmit={handleMFASubmit}
                scope={scope}
                trackingProperties={trackingProperties}
                fixFiMode={fixFiMode}
              />
            )}
          </Box>
        )}
      </Box>
    </>
  );
});

Authenticator.displayName = 'Authenticator';

export default Authenticator;
